import React from 'react';
import { styled } from '@mui/system';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  tooltipClasses
} from '@mui/material';
import { WarningRounded } from '@mui/icons-material';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Link } from 'react-router-dom';

export const CardWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(1)
  },
  [theme.breakpoints.up('md')]: {
    paddingRight: theme.spacing(),
    paddingLeft: theme.spacing(),
    maxWidth: '50%'
  },
  [theme.breakpoints.up('lg')]: {
    paddingRight: theme.spacing(),
    paddingLeft: theme.spacing(),
    maxWidth: '33.33%'
  },
  [theme.breakpoints.up('xl')]: {
    paddingRight: theme.spacing(),
    paddingLeft: theme.spacing(),
    maxWidth: '25%'
  },
  display: 'inline-block'
}));

export const StyledCard = styled(Card)({
  height: '100%',
  textAlign: 'left',
  width: '100%',
  boxSizing: 'border-box',
  verticalAlign: 'top',
  position: 'relative',
  borderRadius: 8,
  boxShadow: 0
});

export const StyledCardHeader = styled(CardHeader, {
  shouldForwardProp: prop => prop !== 'isClickable'
})(({ isClickable }) => ({
  ...(isClickable && {
    '&:hover': {
      backgroundColor: '#f1f1f1',
      cursor: 'pointer'
    }
  })
}));

export const SubheaderContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

export const WarningContainer = styled('div')({
  marginRight: '5px',
  marginLeft: '-4px',
  height: 24,
  display: 'flex'
});

export const StyledWarningIcon = styled(WarningRounded)({
  color: '#FACE31'
});

export const ActionsButton = styled(IconButton)({
  maxHeight: '30px'
});

export const FlexCenterTypography = styled(Typography)({
  display: 'flex',
  alignItems: 'center'
});

export const StyledCardContent = styled(CardContent, {
  shouldForwardProp: prop => prop !== 'isLoading'
})(({ isLoading }) => ({
  borderTop: '1px solid #c4c4c4',
  padding: 0,
  '&:last-child': {
    paddingBottom: 0
  },
  ...(isLoading && {
    textAlign: 'center'
  })
}));

export const GroupLabel = styled(Typography)({
  background: '#d0d0d0',
  textAlign: 'center'
});

export const InfoContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(),
  '& span': {
    whiteSpace: 'nowrap',
    maxWidth: 33
  },
  '& svg': {
    verticalAlign: 'middle'
  },
  '& span span': {
    display: 'inline-block',
    marginTop: 2
  }
}));

export const InfoText = styled('div')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 16,
  width: '33%'
});

export const MetricsWrapper = styled('div')({
  width: '80%',
  display: 'flex',
  justifyContent: 'flex-start'
});

export const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} className={className} classes={{ popper: className }} />
), {
  shouldForwardProp: prop => prop !== 'gutterRight'
})(({ theme, gutterRight }) => ({
  marginRight: gutterRight ? theme.spacing(0.5) : 0,
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    boxShadow: theme.shadows[1],
    fontSize: 14
  }
}));

export const IconContainer = styled('div')({
  width: '20%',
  whiteSpace: 'nowrap',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center'
});

export const ColoredSpan = styled('span', {
  shouldForwardProp: prop => prop !== 'isRed'
})(({ isRed }) => ({
  ...(isRed && {
    color: '#f44336'
  })
}));

export const DeviceInfoLink = styled(Link, {
  shouldForwardProp: prop => prop !== 'isDisabled'
})(({ theme, isDisabled }) => ({
  color: theme.palette.common.black,
  display: 'block',
  textDecoration: 'none',
  '&:hover > div': {
    background: '#f1f1f1'
  },
  ...(isDisabled && {
    cursor: 'not-allowed'
  })
}));

export const NoMeasurementsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(),
  '& span': {
    whiteSpace: 'nowrap',
    maxWidth: 33
  },
  '& svg': {
    verticalAlign: 'middle'
  },
  '& span span': {
    display: 'inline-block',
    marginTop: 2
  },
  alignItems: 'center',
  width: '90%',
  margin: theme.spacing(0, 0, 1, 1)
}));

export const NoDataWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing()
}));

export const NoDataTitle = styled(Typography)({
  width: '60%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  display: 'flex',
  '& span': {
    marginLeft: 6
  }
});

export const NoDataContent = styled(Typography)({
  display: 'flex',
  width: '40%',
  justifyContent: 'space-between'
});

export const StyledNotificationsActiveRoundedIcon = styled(NotificationsActiveRoundedIcon, {
  shouldForwardProp: prop => prop !== 'isRed'
})(({ isRed }) => ({
  ...(isRed && {
    color: '#f44336'
  })
}));

export const StyledAccessTimeIcon = styled(AccessTimeIcon, {
  shouldForwardProp: prop => prop !== 'isRed'
})(({ isRed }) => ({
  ...(isRed && {
    color: '#f44336'
  })
}));
